/*
CRITICAL FREEZE NOTICE — /products (UnifiedProductsPage)
Do NOT modify this page (content, header, SEO, layout) without explicit owner permission.
Owner directive (2025-09-01): Keep title/description static. Filters may only change displayed products.
Any change must start with: "EXPLICIT PERMISSION: PRODUCTS".
*/
import { useState, useEffect } from "react";
import BohoNav from "@/components/BohoNav";
import BohoFooter from "@/components/BohoFooter";
import SEO from "@/components/SEO";
import RecentlyViewed from "@/components/RecentlyViewed";
import { ProductGridManager } from "@/components/ProductGridManager";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Star } from "lucide-react";
import { TruncatedText } from "@/components/TruncatedText";

const UnifiedProductsPage = () => {
  // Hero visibility control from Account toggle (only for /products page)
  const [heroElementsVisible, setHeroElementsVisible] = useState(() => {
    return localStorage.getItem('heroAnimationsEnabled') !== 'false'; // Default to true (show elements)
  });

  // Listen for localStorage changes to update visibility
  useEffect(() => {
    const handleStorageChange = () => {
      setHeroElementsVisible(localStorage.getItem('heroAnimationsEnabled') !== 'false');
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check on component mount in case it changed
    const currentSetting = localStorage.getItem('heroAnimationsEnabled');
    setHeroElementsVisible(currentSetting !== 'false');
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background relative w-full">
      <SEO
        title="Boho Style Co. Curated Bohemian Fashion, Accessories, Home Decor & More"
        description="Browse our complete collection of bohemian clothing, accessories, and decor. Unique handpicked pieces for your free-spirited lifestyle."
        pageType="collection"
      />
      
      {/* Background Wrapper */}
      <div className="relative">
        {/* Background Decorations */}
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-gradient-to-br from-primary/5 via-accent/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-gradient-to-tl from-accent/5 via-primary/5 to-transparent rounded-full blur-3xl" />
        </div>

        {/* Content Layer */}
        <div className="relative min-h-screen flex flex-col">
          <BohoNav />
          
          <div className="flex-grow">
            {/* Page Content */}
            <div className="container-wide py-6">
              
              {/* Intro Text - Above Banner */}
              <div className="mb-6">
                <TruncatedText maxLines={3} className="text-foreground/90 text-base leading-relaxed text-left">
                  Welcome to our complete bohemian collection, where every piece tells a story of artisan craftsmanship and free-spirited style. 
                  From flowing fabrics to intricate details, each item has been thoughtfully curated to bring authentic boho charm to your wardrobe. 
                  Explore timeless pieces that celebrate individuality and embrace the beauty of handcrafted artistry.
                </TruncatedText>
              </div>
              
              {/* Hero Banner - Toggle controlled visibility */}
              {heroElementsVisible && (
                <div className="mb-4 relative">
                  <div className="relative bg-gradient-to-r from-primary/20 via-accent/10 to-primary/20 rounded-lg p-6 border border-primary/30 backdrop-blur-sm overflow-hidden">
                    {/* Decorative corner flourishes */}
                    <div className="absolute top-2 left-4 w-8 h-8 opacity-30">
                      <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                      <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                      <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                    </div>
                    <div className="absolute top-2 right-4 w-8 h-8 opacity-30">
                      <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                      <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                      <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                    </div>
                    <div className="absolute bottom-2 left-4 w-8 h-8 opacity-30">
                      <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                      <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                      <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                    </div>
                    <div className="absolute bottom-2 right-4 w-8 h-8 opacity-30">
                      <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                      <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                      <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                    </div>
                    
                    {/* Depth layers */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 rounded-lg"></div>
                    
                    <div className="relative grid md:grid-cols-12 gap-6 items-center">
                      {/* Main Message - 8 columns */}
                      <div className="md:col-span-8">
                        <div className="flex items-baseline gap-3 mb-2">
                          <h3 className="text-2xl font-boho text-primary font-semibold" style={{WebkitTextStroke: '0.25px hsl(var(--foreground))'}}>Discover Your Perfect Boho Style</h3>
                          <Badge className="bg-gradient-to-r from-primary to-accent text-background text-xs font-medium px-2 py-1 shadow-md border border-border/30 flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-background drop-shadow-sm" />
                            <span className="drop-shadow-sm">Handpicked</span>
                          </Badge>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">
                          Discover our complete collection of bohemian treasures, each piece <span className="font-semibold text-primary">lovingly selected</span> for its unique character and artisan quality.
                        </p>
                      </div>

                      {/* Value Prop - 4 columns */}
                      <div className="md:col-span-4 text-center md:text-right">
                        <div className="relative bg-gradient-to-br from-foreground/30 to-primary/25 backdrop-blur-sm rounded-xl p-4 border-2 border-primary/40 shadow-xl">
                          {/* Decorative inner border */}
                          <div className="absolute inset-2 border border-primary/20 rounded-lg pointer-events-none"></div>
                          
                          <div className="text-2xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent mb-1" style={{WebkitTextStroke: '0.25px hsl(var(--foreground))'}}>
                            Free Spirit
                          </div>
                          <div className="text-xs text-foreground font-medium mb-2 drop-shadow-sm">Express your unique style</div>
                          <div className="flex items-center justify-center md:justify-end gap-2 text-xs text-background font-medium bg-accent/80 rounded-full px-2 py-1 border border-accent/40">
                            <Star className="w-3 h-3 text-background drop-shadow-sm" />
                            <span className="drop-shadow-sm">Authentic boho lifestyle</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Grid Manager - All grid logic delegated */}
              <ProductGridManager
                enableFullCatalog={true}
                showSearch={true}
                showSidebar={true}
                showSortControl={true}
                showPagination={true}
                enablePriceFilter={true}
                enableSizeFilter={true}
                enableColorFilter={true}
                enableStockFilter={true}
                enableCategoryFilter={true}
                enableSubCategoryFilters={true}
                enableUrlSync={true}
                enableScrollRestoration={true}
                productsPerPage={24}
                defaultSortBy="updated"
              />
            </div>
          </div>
          
          {/* Recently Viewed Section - Above Footer */}
          <div className="container-wide pb-8">
            <RecentlyViewed maxItems={24} />
          </div>
          
          <BohoFooter />
        </div>
      </div>
    </div>
  );
};

export default UnifiedProductsPage;
